/**
 * KDS theme config and composable system factory.
 *
 * Consumer apps extend the KDS theme by passing app-specific recipe overrides:
 *
 * ```ts
 * import { createExtendedSystem } from '@kintsugi/ui/theme';
 *
 * export default createExtendedSystem({
 *   recipes: { badge: badgeRecipe },
 *   slotRecipes: { drawer: drawerSlotRecipe, toast: toastSlotRecipe },
 * });
 * ```
 *
 * Calling with no arguments returns the base KDS system (equivalent to the
 * KDS Provider's theme).
 */

import { createSystem, defaultConfig } from '@chakra-ui/react';

import { alertSlotRecipe } from '../components/alert/alert.slot-recipe';
import { avatarSlotRecipe } from '../components/avatar/avatar.slot-recipe';
import { buttonRecipe } from '../components/button/button.recipe';
import { checkboxSlotRecipe } from '../components/checkbox/checkbox.slot-recipe';
import { dataTableSlotRecipe } from '../components/data-table/data-table.slot-recipe';
import { datePickerSlotRecipe } from '../components/date-picker/date-picker.slot-recipe';
import { drawerSlotRecipe } from '../components/drawer/drawer.slot-recipe';
import { inlineTableHeaderRecipe } from '../components/inline-table-header/inline-table-header.recipe';
import { inputRecipe } from '../components/text-field/text-field.recipe';
import { fieldSlotRecipe } from '../components/text-field/text-field.slot-recipe';
import { menuSlotRecipe } from '../components/menu/menu.slot-recipe';
import { modalSlotRecipe } from '../components/modal/modal.slot-recipe';
import { nativeSelectSlotRecipe } from '../components/select/native-select.slot-recipe';
import { paginationSlotRecipe } from '../components/pagination/pagination.slot-recipe';
import { progressBarSlotRecipe } from '../components/progress-bar/progress-bar.slot-recipe';
import { ratingSlotRecipe } from '../components/rating/rating.slot-recipe';
import { selectSlotRecipe } from '../components/select/select.slot-recipe';
import { spinnerRecipe } from '../components/spinner/spinner.recipe';
import { statusBadgeRecipe } from '../components/status-badge/status-badge.recipe';
import { switchSlotRecipe } from '../components/switch/switch.slot-recipe';
import { tabsSlotRecipe } from '../components/tabs/tabs.slot-recipe';
import { textAreaRecipe } from '../components/text-area/text-area.recipe';
import { tooltipSlotRecipe } from '../components/tooltip/tooltip.slot-recipe';
import { globalCss } from './global-css';
import { iconRecipe } from './recipes/icon.recipe';
import { popoverSlotRecipe } from './recipes/popover.slot-recipe';
import { toastSlotRecipe } from './recipes/toast.slot-recipe';
import { semanticColors } from './semantic/colors';
import { BORDER_WIDTHS } from './tokens/border-widths';
import { BORDERS } from './tokens/borders';
import { breakpoints } from './tokens/breakpoints';
import { colors } from './tokens/colors';
import { GRADIENTS } from './tokens/gradients';
import { RADII } from './tokens/radii';
import { shadows } from './tokens/shadows';
import { textStyles, typography } from './tokens/typography';

const kdsRecipes = {
  button: buttonRecipe,
  icon: iconRecipe,
  inlineTableHeader: inlineTableHeaderRecipe,
  input: inputRecipe,
  spinner: spinnerRecipe,
  statusBadge: statusBadgeRecipe,
  textarea: textAreaRecipe,
};

const kdsSlotRecipes = {
  alert: alertSlotRecipe,
  avatar: avatarSlotRecipe,
  checkbox: checkboxSlotRecipe,
  dialog: modalSlotRecipe,
  drawer: drawerSlotRecipe,
  field: fieldSlotRecipe,
  nativeSelect: nativeSelectSlotRecipe,
  progress: progressBarSlotRecipe,
  ratingGroup: ratingSlotRecipe,
  select: selectSlotRecipe,
  dataTable: dataTableSlotRecipe,
  datePicker: datePickerSlotRecipe,
  menu: menuSlotRecipe,
  pagination: paginationSlotRecipe,
  popover: popoverSlotRecipe,
  switch: switchSlotRecipe,
  tabs: tabsSlotRecipe,
  toast: toastSlotRecipe,
  tooltip: tooltipSlotRecipe,
};

interface ExtendedSystemOverrides {
  recipes?: Record<string, unknown>;
  slotRecipes?: Record<string, unknown>;
}

/**
 * Create a Chakra system that starts from the full KDS theme and merges in
 * app-specific recipe overrides.  Tokens, semantic colors, text styles, and
 * global CSS are always inherited from KDS.
 */
export function createExtendedSystem(overrides: ExtendedSystemOverrides = {}) {
  return createSystem(defaultConfig, {
    cssVarsPrefix: 'ks',
    globalCss,
    theme: {
      breakpoints,
      semanticTokens: {
        shadows,
        colors: semanticColors,
      },
      recipes: {
        ...kdsRecipes,
        ...overrides.recipes,
      },
      slotRecipes: {
        ...kdsSlotRecipes,
        ...overrides.slotRecipes,
      },
      textStyles,
      tokens: {
        ...typography,
        colors,
        borders: BORDERS,
        borderWidths: BORDER_WIDTHS,
        radii: RADII,
        // GRADIENTS is a defineTokens() result and already wrapped in a
        // `gradients` key — spreading avoids registering `gradients.gradients.*`.
        ...GRADIENTS,
      },
    },
  });
}
